import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { UserSettingsKey } from 'webapp/enums/user-settings';

import { viewSettingsNamePrefix } from 'webapp/models-custom/filter-sort/goal-okrs';
import type Goal from 'webapp/models-custom/goal';

export default class GoalsStrategyController extends Controller {
	@service('account') declare accountService: Service.Account;
	@service('view-settings-manager')
	declare viewSettingsManagerService: Service.ViewSettingsManager;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	@tracked _viewId = 'table';
	@tracked filterSortGoals: Goal[] = [];

	get viewId() {
		return this._viewId;
	}

	set viewId(value: string) {
		this._viewId = value;
	}

	override get model(): Goal[] {
		return this.mmGoalsService.goalForSpaceModels.filter((goal) => goal.isPublic);
	}

	get settingsName(): string {
		return `${viewSettingsNamePrefix}-${this.accountService.id}`;
	}

	get isTableView(): boolean {
		return this.viewId === 'table';
	}

	get showGoalsList(): boolean {
		return this.model.length > 0;
	}

	get showEmptyPage() {
		return !this.mmGoalsService.isGoalsOKRsEnabled;
	}

	get isUserSettingOverviewHidden(): boolean {
		return this.userSettingsService.isOkrOverviewHidden;
	}

	get isOverviewVisible(): boolean {
		return !this.isUserSettingOverviewHidden && this.isTableView;
	}

	get showEmptyState(): boolean {
		return !this.showGoalsList;
	}

	loadViewSettings(): void {
		const savedValues =
			this.viewSettingsManagerService.load<{ viewId: string }>(this.settingsName, true) || {};
		if (savedValues.viewId) {
			this.viewId = savedValues.viewId;
		}
	}

	@action
	onFilterSortChange(filterSortGoals: Goal[]): void {
		this.filterSortGoals = filterSortGoals;
	}

	@action
	toggleOverview(): void {
		void this.userSettingsService.save(
			UserSettingsKey.OKR_OVERVIEW_HIDDEN,
			!this.isUserSettingOverviewHidden,
		);
	}

	@action
	openView(viewId: string): void {
		this.viewId = viewId;
		this.viewSettingsManagerService.save(this.settingsName, { viewId }, true);
	}

	@action
	createNewGoal(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const model = this.mmGoalsService.createNewModel(),
			trackingOrigin = 'goals';

		this.mmGoalsService.openCreatePopup(model, trackingOrigin);
	}
}
